import type { Dispatch, SetStateAction } from 'react';
import type { PayPalElementProps } from '@adyen/adyen-web/dist/types/components/PayPal/types';
import type { FetchResult } from '@apollo/client';
import { getOnOrderPlacedCaptureConfig } from '@shared/utils/src/adyen';
import { storeKeyMapper } from '@shared/utils/src/ct';
import type { Cart } from '../../../codegen/types';
import type { PlaceOrderMutation } from '../../../graphqlDocument/order/placeOrder.generated';
import { sendAddPaymentInfoEvent } from '../../../utils/gtm/events/add_payment_info/sendAddPaymentInfoEvent';
import { localeToUpperCase } from '../../../utils/internationalization';
import { setPaymentMethodInStorage } from '../utils/setPaymentMethodInStorage';

// Adyen docs - web components - PayPal
// https://docs.adyen.com/payment-methods/paypal/web-component/

export interface CreatePayPalConfigProps {
  locale: string;
  miniCart: Cart | Record<string, never>;
  cartId: string;
  baseUrl: string;
  setIsPaymentBtnDisabled: Dispatch<SetStateAction<boolean>>;
  setPaymentInProgress: Dispatch<SetStateAction<boolean>>;
  setPaymentError: Dispatch<SetStateAction<boolean>>;
  validateDiscountsBeforePayment: () => Promise<boolean>;
  placeOrderMutation: () => Promise<FetchResult<PlaceOrderMutation, Record<string, unknown>, Record<string, unknown>>>;
  onBeforeSubmit: (data: unknown, actions: unknown) => Promise<void>;
}

export const createPayPalConfig = ({
  locale,
  miniCart,
  cartId,
  baseUrl,
  setIsPaymentBtnDisabled,
  setPaymentInProgress,
  setPaymentError,
  validateDiscountsBeforePayment,
  placeOrderMutation,
  onBeforeSubmit,
}: CreatePayPalConfigProps): PayPalElementProps => ({
  intent: getOnOrderPlacedCaptureConfig(storeKeyMapper(locale))?.has('paypal') ? 'capture' : 'authorize',
  blockPayPalPayLaterButton: true,
  blockPayPalCreditButton: true,
  showPayButton: true,
  style: {
    color: 'blue',
    label: 'checkout',
  },
  blockPayPalVenmoButton: true,
  locale: localeToUpperCase(locale),
  beforeSubmit: async (data, _, actions) => {
    setIsPaymentBtnDisabled(true);
    setPaymentInProgress(true);

    const isDiscountValid = await validateDiscountsBeforePayment();

    if (!isDiscountValid) {
      actions.reject();

      setPaymentInProgress(false);
      setIsPaymentBtnDisabled(false);

      return;
    }

    const result = await placeOrderMutation();

    if (result.data?.placeOrder.accepted) {
      sendAddPaymentInfoEvent(miniCart, 'paypal', cartId, baseUrl);
      setPaymentMethodInStorage('paypal', 'PayPal');
      onBeforeSubmit(data, actions);
    } else {
      actions.reject();

      setPaymentError(true);
      setIsPaymentBtnDisabled(false);
      setPaymentInProgress(false);
    }
  },
});
